/**
 * LongPostChunkCard Component
 * 
 * A draggable card component that displays individual chunk information including:
 * - Text content preview with word count
 * - Visual status indicators (title chunks, completion progress)
 * - Action buttons (review, edit, translate)
 * - Drag handle for reordering chunks
 * - Context menu for additional actions (delete)
 * - Responsive design with proper spacing
 * 
 * Used in: LongPostChunkManager for displaying chunk list
 */
import React from 'react';
import { Draggable } from 'react-beautiful-dnd';
import {
    ListItem,
    Paper,
    Box,
    IconButton,
    Avatar,
    Typography,
    Chip,
    Tooltip
} from '@mui/material';
import {
    DragHandle,
    Edit,
    Visibility,
    Translate,
    MoreVert,
    FindInPage
} from '@mui/icons-material';

const LongPostChunkCard = ({ 
    chunk, 
    index, 
    onReview, 
    onEdit, 
    onTranslate, 
    onCheckTranslations,
    onMenuOpen 
}) => {
    return (
        <Draggable key={chunk._id} draggableId={chunk._id} index={index}>
            {(provided, snapshot) => (
                <ListItem
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    component={Paper}
                    sx={{
                        mb: 2,
                        p: 0,
                        backgroundColor: snapshot.isDragging ? '#f5f5f5' : 'white',
                        border: chunk.isTitle ? '2px solid #1976d2' : '1px solid #e0e0e0',
                        borderRadius: 2,
                        overflow: 'hidden',
                        transition: 'transform 0.2s, box-shadow 0.2s',
                        '&:hover': {
                            transform: snapshot.isDragging ? 'none' : 'translateY(-1px)',
                            boxShadow: snapshot.isDragging ? 'none' : 3
                        }
                    }}
                >
                    <Box display="flex" alignItems="center" width="100%" p={2}>
                        {/* Drag Handle */}
                        <IconButton
                            {...provided.dragHandleProps}
                            sx={{ 
                                mr: 2, 
                                color: 'primary.main',
                                cursor: 'grab',
                                '&:active': { cursor: 'grabbing' }
                            }}
                        >
                            <DragHandle />
                        </IconButton>

                        {/* Order Badge */}
                        <Avatar
                            sx={{
                                width: 40,
                                height: 40,
                                mr: 2,
                                backgroundColor: chunk.isTitle ? 'primary.main' : 'secondary.main',
                                fontSize: '0.9rem',
                                fontWeight: 'bold'
                            }}
                        >
                            {chunk.order}
                        </Avatar>

                        {/* Content */}
                        <Box sx={{ flexGrow: 1 }}>
                            <Box display="flex" alignItems="center" gap={1} mb={1}>
                                {chunk.isTitle && (
                                    <Chip size="small" label="Title" color="primary" />
                                )}
                            </Box>

                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                {chunk.text?.text
                                    ? `${chunk.text.text.substring(0, 120)}...`
                                    : 'No content added yet'
                                }
                            </Typography>

                            {chunk.text?.text && (
                                <Typography variant="caption" color="text.secondary">
                                    {chunk.text.text.length} characters • 
                                    {chunk.text.completionProgress || 0}% translated
                                </Typography>
                            )}
                        </Box>

                        {/* Action Buttons */}
                        <Box display="flex" gap={1} alignItems="center">
                            <Tooltip title="Review Content">
                                <IconButton
                                    onClick={() => onReview(chunk)}
                                    disabled={!chunk.text?.text}
                                    color="primary"
                                >
                                    <Visibility />
                                </IconButton>
                            </Tooltip>

                            <Tooltip title="Edit Chunk">
                                <IconButton
                                    onClick={() => onEdit(chunk)}
                                    color="secondary"
                                >
                                    <Edit />
                                </IconButton>
                            </Tooltip>

                            <Tooltip title="Check Missing Translations">
                                <IconButton
                                    onClick={() => onCheckTranslations(chunk)}
                                    disabled={!chunk.text?.text}
                                    color="secondary"
                                >
                                    <FindInPage />
                                </IconButton>
                            </Tooltip>

                            <Tooltip title="Translate">
                                <IconButton
                                    onClick={() => onTranslate(chunk)}
                                    disabled={!chunk.text?.text}
                                    sx={{ color: 'success.main' }}
                                >
                                    <Translate />
                                </IconButton>
                            </Tooltip>

                            <IconButton
                                onClick={(e) => onMenuOpen(e, chunk)}
                            >
                                <MoreVert />
                            </IconButton>
                        </Box>
                    </Box>
                </ListItem>
            )}
        </Draggable>
    );
};

export default LongPostChunkCard; 